import {ExperimentsBag} from '@wix/wix-experiments'
import {bindActionCreators, PreloadedState} from 'redux'
import {ControllerParams, CreateControllerFn} from '@wix/yoshi-flow-editor'
import {EVENTS_APP_ID, isRtlLanguage} from '@wix/wix-events-commons-statics'
import {createUouBiMiddlewareWithBiParams, createUsersBiMiddlewareWithBiParams} from '../../commons/bi/bi'
import {setBaseEnvironment, setFormFactor} from '../../commons/actions/environment'
import {updateSiteSettings} from '../../commons/actions/site-settings'
import {UserRole} from '../../commons/enums'
import {decodeInstance} from '../../commons/selectors/instance'
import {getMultilingualInitialState} from '../../commons/services/multilingual'
import {getQueryParams} from '../../commons/services/navigation'
import {isMembersEventsPageInstalled} from '../../commons/utils/members-api'
import {createReduxStore, subscribeToStateChanges} from '../../commons/utils/store'
import {getLanguage, getPageInfo, isEditor, isMobile} from '../../commons/utils/wix-code-api'
import {watchInstance} from '../../commons/actions/instance'
import {isResponsiveEditor} from '../../commons/selectors/environment'
import {Actions, State, StoreExtraArgs} from './types/state'
import {userEventsLogger} from './user-events-logger'
import {Api} from './utils/api'
import {isPaidPlansInstalled} from './services/apps'
import {sortEvents} from './reducers/events'
import reducers from './reducers'
import {datesMiddleware} from './middlewares/date'
import * as eventsUsers from './bi/users-bi-events-map'
import * as eventsUou from './bi/uou-bi-events-map'
import {updateSettings, updateStyle} from './actions/sdk'
import {resized} from './actions/resize'
import {navigateToPage, onLinkNavigatedToPage} from './actions/navigate-to-page'
import {addLoginListener, loadMembersForEvents, openMembersModal} from './actions/members'
import {closeListLayoutItems, openListLayoutItem, widgetLoaded} from './actions/layout'
import {loadEventsPage} from './actions/events'
import {createEvent, shareEvent} from './actions/event'
import {updateComponent, updateComponentDraft, updateStyleParams} from './actions/component'
import {
  closeMonthlyCalendarEvent,
  closeMonthlyCalendarPopup,
  openMonthlyCalendarEvent,
  openMonthlyCalendarPopup,
  subCalendarMonth,
  addCalendarMonth,
  resetCalendar,
} from './actions/calendar-layout'

const createWidgetController: CreateControllerFn = async (controllerParams: ControllerParams) => {
  return {
    pageReady: () => pageReady(controllerParams),
  }
}

const pageReady = async (controllerParams: ControllerParams) => {
  const {controllerConfig: controller} = controllerParams
  const serverApi = new Api(controllerParams)
  const language = getLanguage(controller.wixCodeApi)
  const experiments = controllerParams.flowAPI.experiments.all()

  const [pageInfo, pageUrl] = await Promise.all([
    getPageInfo(controller.wixCodeApi),
    controller.wixCodeApi.site.getSectionUrl({
      sectionId: 'events',
      appDefinitionId: EVENTS_APP_ID,
    }),
  ])
  const store = await createStore(controller, serverApi, experiments)
  const state = store.getState()

  const props = {
    pageUrl,
    baseUrl: controller.wixCodeApi.location.baseUrl,
    queryParams: getQueryParams(controller.wixCodeApi),
    pageInfo,
    state,
    actions: exportedActions({
      store,
    }),
    isRTL: isRtlLanguage(language),
  }

  if (state.membersEnabled) {
    serverApi.onLogin(() => {
      store.dispatch(loadMembersForEvents() as any)
    })
  }

  controller.setProps(props)
}

const createStore = async (
  controller: ControllerParams['controllerConfig'],
  serverApi: Api,
  experiments: ExperimentsBag,
) => {
  const {wixCodeApi, platformAPIs, appParams, compId, config} = controller
  const initialData = await getInitialData(serverApi, controller, experiments)

  const biParams = {
    wixCodeApi,
    platformAPIs,
    appParams,
    compId,
    user: {
      aid: initialData.instance.aid,
      uid: initialData.instance.uid,
    },
  }
  const biMiddleware = [
    createUsersBiMiddlewareWithBiParams(biParams, eventsUsers),
    createUouBiMiddlewareWithBiParams(biParams, eventsUou),
  ]

  const userEventsLoggerMiddleware = userEventsLogger({wixCodeApi})

  const store = createReduxStore<State, StoreExtraArgs>({
    reducers,
    initialData,
    extraArguments: {
      serverApi,
      wixCodeApi,
      compId,
    },
    middleware: [...biMiddleware, userEventsLoggerMiddleware, datesMiddleware],
  })

  store.dispatch(updateStyleParams(config.style.styleParams))
  store.dispatch(setBaseEnvironment() as any)
  store.dispatch(addLoginListener() as any)

  watchInstance(controller, store.dispatch)
  subscribeToStateChanges(controller, store)

  return store
}

const getInitialData = async (
  serverApi: Api,
  {appParams, wixCodeApi, config: controllerConfig}: ControllerParams['controllerConfig'],
  experiments: ExperimentsBag,
): Promise<PreloadedState<State>> => {
  const [membersEnabled, paidPlansInstalled] = await Promise.all([
    isMembersEventsPageInstalled(wixCodeApi),
    isPaidPlansInstalled(wixCodeApi),
  ])

  const instance = appParams.instance
  const decodedInstance = decodeInstance(instance)
  const isTemplate = decodedInstance.siteIsTemplate
  const showcase = isTemplate || isEditor(wixCodeApi) || isMobile(wixCodeApi)
  const {widgetType, listLayout, recurringFilter} = controllerConfig.style.styleParams.numbers

  const {
    component: {events = [], config, id},
    siteSettings,
    demoEvents,
    hasMoreEvents,
    dates,
  } = await serverApi.getAppData(
    membersEnabled,
    paidPlansInstalled,
    isResponsiveEditor(controllerConfig),
    showcase,
    widgetType,
    listLayout,
    recurringFilter,
  )

  return {
    experiments,
    events: {
      events: sortEvents(events),
      hasMore: hasMoreEvents,
      moreLoading: false,
    },
    siteSettings,
    demoEvents,
    multilingual: getMultilingualInitialState(wixCodeApi),
    component: {
      ...config,
      id,
    },
    membersEnabled,
    paidPlansEnabled: paidPlansInstalled,
    memberLoggedIn: wixCodeApi.user.currentUser.loggedIn,
    owner: wixCodeApi.user.currentUser.role === UserRole.ADMIN,
    instance: {
      instance,
      ...decodedInstance,
    },
    calendarLayout: {
      ...(dates.calendar ?? {}),
      loadedPages: dates.calendar?.referenceDate ? [dates.calendar.referenceDate] : [],
    },
    dates,
  } as PreloadedState<State>
}

const exportedActions = ({store}) => {
  const dispatchActions = {
    addCalendarMonth,
    subCalendarMonth,
    resetCalendar,
    createEvent,
    updateSiteSettings,
    updateComponent,
    updateComponentDraft,
    navigateToPage,
    onLinkNavigatedToPage,
    updateStyle,
    updateStyleParams,
    updateSettings,
    setBaseEnvironment,
    openListLayoutItem,
    closeListLayoutItems,
    openMonthlyCalendarPopup,
    closeMonthlyCalendarPopup,
    openMonthlyCalendarEvent,
    closeMonthlyCalendarEvent,
    widgetLoaded,
    openMembersModal,
    shareEvent,
    resized,
    setFormFactor,
    loadEventsPage,
  }

  const actions: Actions = {
    ...bindActionCreators(dispatchActions, store.dispatch),
  }
  return actions
}

export default createWidgetController
